import type { TaskTemplateCategory } from "./types";

export const MSP_SCHOLEY_MITCHELL_TEMPLATES: TaskTemplateCategory = {
  id: "schooley-mitchell",
  name: "Schooley Mitchell (MECA SM)",
  description: "Templates for Schooley Mitchell cost optimization business",
  templates: [
    {
      id: "post-audit-followup",
      name: "Post-Audit Follow-up",
      description:
        "Follow up with client after receiving audit results and send to client with invoice",
      title: "Post-Audit Follow-up: [Client Name]",
      description_text: `Complete post-audit follow-up process:

**Immediate Actions:**
- [ ] Review audit results and findings
- [ ] Prepare client communication package
- [ ] Send audit results to client via email
- [ ] Generate and send invoice for audit services
- [ ] Schedule follow-up call to discuss results

**Client Communication:**
- [ ] Draft professional email with audit summary
- [ ] Include key findings and recommendations
- [ ] Attach detailed audit report
- [ ] Highlight potential cost savings identified
- [ ] Set expectations for next steps

**Invoice & Billing:**
- [ ] Generate invoice for audit services
- [ ] Include detailed breakdown of services provided
- [ ] Send invoice via email with audit results
- [ ] Follow up on payment if needed

**Next Steps:**
- [ ] Schedule client call to discuss findings
- [ ] Prepare for potential implementation phase
- [ ] Document all communications in CRM`,
      tags: ["meca_sm", "post-audit", "audit-followup", "client-coordination", "cost-optimization"],
      priority: "high",
      status: "pending",
      category: "schooley-mitchell",
    },
    {
      id: "online-access-setup",
      name: "Online Access Setup",
      description:
        "Coordinate with client to get online access to their accounts for cost optimization",
      title: "Online Access Setup: [Client Name]",
      description_text: `Coordinate online access setup with client:

**Initial Contact:**
- [ ] Call client to explain online access benefits
- [ ] Schedule convenient time for setup call
- [ ] Send preliminary instructions via email
- [ ] Prepare list of accounts we need access to

**Account Access Coordination:**
- [ ] Identify all utility and service accounts
- [ ] Coordinate with client's IT team if needed
- [ ] Set up secure access credentials
- [ ] Test access to all required accounts
- [ ] Document access procedures

**Client Communication:**
- [ ] Explain security measures and data protection
- [ ] Provide timeline for cost optimization analysis
- [ ] Set expectations for regular updates
- [ ] Schedule follow-up meetings

**Documentation:**
- [ ] Document all account access details securely
- [ ] Create access log for compliance
- [ ] Set up monitoring for account changes
- [ ] Prepare initial analysis framework`,
      tags: [
        "meca_sm",
        "online-access",
        "account-setup",
        "client-coordination",
        "cost-optimization",
      ],
      priority: "high",
      status: "pending",
      category: "schooley-mitchell",
    },
    {
      id: "client-coordination-call",
      name: "Client Coordination Call",
      description:
        "Schedule and conduct coordination call with client for account access or follow-up",
      title: "Client Coordination Call: [Client Name]",
      description_text: `Conduct client coordination call:

**Pre-Call Preparation:**
- [ ] Review client file and previous communications
- [ ] Prepare agenda and talking points
- [ ] Gather any required documents or information
- [ ] Test technology and meeting setup

**Call Agenda:**
- [ ] Welcome and introductions
- [ ] Review current status and progress
- [ ] Discuss next steps and timeline
- [ ] Address any client questions or concerns
- [ ] Schedule follow-up actions

**Post-Call Actions:**
- [ ] Send meeting summary via email
- [ ] Document all decisions and action items
- [ ] Update CRM with call notes
- [ ] Schedule any necessary follow-up tasks
- [ ] Send any promised materials or information

**Follow-up:**
- [ ] Complete any action items from call
- [ ] Follow up on any outstanding items
- [ ] Prepare for next phase of engagement`,
      tags: ["meca_sm", "client-coordination", "follow-up", "cost-optimization"],
      priority: "medium",
      status: "pending",
      category: "schooley-mitchell",
    },
    {
      id: "cost-optimization-analysis",
      name: "Cost Optimization Analysis",
      description: "Analyze client accounts and identify cost optimization opportunities",
      title: "Cost Optimization Analysis: [Client Name]",
      description_text: `Conduct comprehensive cost optimization analysis:

**Data Collection:**
- [ ] Access all client utility and service accounts
- [ ] Gather current pricing and contract details
- [ ] Identify usage patterns and trends
- [ ] Collect competitor pricing information
- [ ] Review contract terms and renewal dates

**Analysis Phase:**
- [ ] Compare current rates with market rates
- [ ] Identify potential savings opportunities
- [ ] Calculate potential annual savings
- [ ] Prioritize opportunities by impact
- [ ] Prepare detailed recommendations

**Report Preparation:**
- [ ] Create executive summary of findings
- [ ] Develop detailed cost comparison charts
- [ ] Prepare implementation timeline
- [ ] Calculate ROI for each recommendation
- [ ] Include risk assessment and mitigation

**Client Presentation:**
- [ ] Schedule presentation meeting
- [ ] Prepare visual aids and charts
- [ ] Practice presentation delivery
- [ ] Prepare for client questions
- [ ] Follow up with detailed written report`,
      tags: ["meca_sm", "cost-optimization", "analysis", "client-coordination"],
      priority: "high",
      status: "pending",
      category: "schooley-mitchell",
    },
  ],
};
